import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { APP_URL } from "@/config/urls";
import { IS_MULTI_TENANT } from "./tenancy";
import { resolveTenantKeyFromHost } from "./tenantKey";
import { getBearerTokenFromCookie, setCookie } from "./utils";

/**
 * Multi-tenant only: stamp the request with the tenant resolved from the current
 * host. No-op in standalone mode, so the header is never sent for single-tenant
 * builds and behaviour matches the original axios setup exactly.
 */
function attachTenantKey(config: InternalAxiosRequestConfig) {
  if (!IS_MULTI_TENANT) return config;
  if (typeof window === "undefined") return config;
  const key = resolveTenantKeyFromHost(window.location.host);
  if (key) {
    config.headers.set("X-Tenant-Key", key);
  }
  return config;
}

/** Multi-tenant only: tenant header for the standalone (interceptor-less) refresh call. */
function tenantHeader(): Record<string, string> {
  if (!IS_MULTI_TENANT || typeof window === "undefined") return {};
  const key = resolveTenantKeyFromHost(window.location.host);
  return key ? { "X-Tenant-Key": key } : {};
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TOKEN_EXPIRY_HOURS = 9;
const TOKEN_EXPIRY_SECONDS = TOKEN_EXPIRY_HOURS * 60 * 60;

const REFRESH_TOKEN_ENDPOINT = "/user/refresh-token";
const SIGN_IN_PATH = "/sign-in";

// ============================================================================
// PUBLIC AXIOS INSTANCE
// ============================================================================

/**
 * Public Axios instance for unauthenticated API calls
 * Use for: Public content, page data, anonymous endpoints
 */
export const publicAxios = axios.create({
  baseURL: APP_URL.API_BASE_URL,
  withCredentials: true,
});

publicAxios.interceptors.request.use(
  (config) => attachTenantKey(config),
  (error) => Promise.reject(error)
);

// ============================================================================
// TOKEN REFRESH QUEUE
// ============================================================================

interface QueuedRequest {
  resolve: (value: string) => void;
  reject: (reason?: any) => void;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

/**
 * Process all queued requests after token refresh
 */
const processQueue = (error: AxiosError | null, token: string | null = null): void => {
  failedQueue.forEach((request) => {
    if (error) {
      request.reject(error);
    } else if (token) {
      request.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Add request to queue while token is being refreshed
 */
const queueRequest = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });
};

// ============================================================================
// TOKEN REFRESH LOGIC
// ============================================================================

/**
 * Attempt to refresh the expired JWT token
 */
const refreshAuthToken = async (expiredToken: string): Promise<string> => {
  const response = await axios.post(
    `${APP_URL.API_BASE_URL}${REFRESH_TOKEN_ENDPOINT}`,
    {},
    {
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${expiredToken}`,
        ...tenantHeader(),
      },
    }
  );

  const newToken = response.data?.data?.token;

  if (!newToken) {
    throw new Error("No token received from refresh endpoint");
  }

  return newToken;
};

/**
 * Clear all authentication data and redirect to login
 */
const handleAuthFailure = (): void => {
  // Clear cookies by setting expired date
  document.cookie = "Bearer=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie = "userRole=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  if (typeof window !== "undefined") {
    window.location.href = SIGN_IN_PATH;
  }
};

// ============================================================================
// ADMIN AXIOS INSTANCE
// ============================================================================

/**
 * Admin Axios instance for authenticated API calls
 * Features:
 * - Automatic Authorization header injection
 * - Automatic token refresh on 401
 * - Request queuing during refresh
 * Use for: Admin CRUD operations, protected endpoints
 */
const adminAxios = axios.create({
  baseURL: APP_URL.API_BASE_URL,
  withCredentials: true,
});

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

adminAxios.interceptors.request.use(
  (config) => {
    attachTenantKey(config);
    const token = getBearerTokenFromCookie("Bearer");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

adminAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        try {
          const token = await queueRequest();
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return adminAxios(originalRequest);
        } catch (queueError) {
          return Promise.reject(queueError);
        }
      }

      // Mark request as retry attempt
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Get expired token
        const expiredToken = getBearerTokenFromCookie("Bearer");

        if (!expiredToken) {
          throw new Error("No token available for refresh");
        }

        // Refresh the token
        const newToken = await refreshAuthToken(expiredToken);

        // Update cookie with new token
        setCookie({
          name: "Bearer",
          value: newToken,
          maxAge: TOKEN_EXPIRY_SECONDS
        });

        // Update original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        // Process all queued requests
        processQueue(null, newToken);

        isRefreshing = false;

        // Retry the original request
        return adminAxios(originalRequest);

      } catch (refreshError) {
        // Refresh failed - clear auth and redirect
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;
        handleAuthFailure();

        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden - Insufficient permissions
    if (error.response?.status === 403) {
      console.error("❌ Access forbidden - insufficient permissions");
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// EXPORTS
// ============================================================================

export default adminAxios;
